import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import FavoritesController from '@jwp/ott-common/src/controllers/FavoritesController';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { useProfileStore } from '@jwp/ott-common/src/stores/ProfileStore';
import { ACCESS_MODEL, COOKIE_DOMAIN } from '@jwp/ott-common/src/constants';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useProfiles } from '@jwp/ott-hooks-react/src/useProfiles';
import AccountCircle from '@jwp/ott-theme/assets/icons/account_circle.svg?react';
import LibraryBooksIcon from '@jwp/ott-theme/assets/icons/library_books.svg?react';
import Close from '@jwp/ott-theme/assets/icons/close.svg?react';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import Cookies from 'js-cookie';
import env from '@jwp/ott-common/src/env';

import Exit from '@jwp/ott-theme/assets/icons/exit.svg?react';
import Favorite from '@jwp/ott-theme/assets/icons/favorite.svg?react';
import {
  RELATIVE_PATH_USER_ACCOUNT,
  RELATIVE_PATH_USER_FAVORITES,
  RELATIVE_PATH_USER_MY_PROFILE,
  RELATIVE_PATH_USER_PAYMENTS,
  RELATIVE_PATH_USER_LIBRARY_ACCESS,
} from '@jwp/ott-common/src/paths';
import { userProfileURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { useFavoritesStore } from '@jwp/ott-common/src/stores/FavoritesStore';

import AccountComponent from '../../components/Account/Account';
import Button from '../../components/Button/Button';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import Favorites from '../../components/Favorites/Favorites';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import PaymentContainer from '../../containers/PaymentContainer/PaymentContainer';
import EditProfile from '../../containers/Profiles/EditProfile';
import Icon from '../../components/Icon/Icon';

import styles from './User.module.scss';
import { useMutation } from 'react-query';
import { signInUser } from '@jwp/ott-common/src/controllers/graphql';

const User = (): JSX.Element => {
  const favoritesController = getModule(FavoritesController);
  const accountController = getModule(AccountController);

  const isProduction = import.meta.env.APP_ENVIRONMENT === 'PRODUCTION';

  const { accessModel, favoritesList } = useConfigStore(
    (s) => ({
      accessModel: s.accessModel,
      favoritesList: s.config.features?.favoritesList,
    }),
    shallow,
  );
  const navigate = useNavigate();
  const { t } = useTranslation(['user', 'account']);
  const breakpoint = useBreakpoint();
  const [clearFavoritesOpen, setClearFavoritesOpen] = useState(false);
  const location = useLocation();

  const isLargeScreen = breakpoint > Breakpoint.md;
  const { user: customer, subscription, loading } = useAccountStore();

  const { canUpdateEmail } = accountController.getFeatures();
  const { profile } = useProfileStore();
  const favorites = useFavoritesStore((state) => state.getPlaylist());
  const { profilesEnabled } = useProfiles();

  const { SelectedLibraryData: { libraryList = [] } = {} } = JSON.parse(localStorage.getItem('SelectedLibraryData') || '{}');
  const libraryParent = libraryList.map((item: any) => item.libraryParent.libraryName);
  const libraryBranchName = libraryList.flatMap((item: any) => item.libraryBranch.map((branch: any) => branch.libraryName));
  const libraryBranchId = libraryList.flatMap((item: any) => item.libraryBranch.map((branch: any) => branch.libraryId));
  const libraryType = libraryList.flatMap((item: any) => item.libraryBranch.map((branch: any) => branch.libraryType));
  const subscriptionDetail = JSON.parse(localStorage.getItem('SelectedLibraryData') || '{}');
  const libraryParents = libraryList.map((item: any) => item.restriction);
  const withInOrganization = libraryList.map((item: any) => item.withInOrganization);
  const date = new Date(subscriptionDetail?.SelectedLibraryData?.subscriptionDetail?.expireAt);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const formattedDate = `${year}-${month}-${day}`;

  const profileAndFavoritesPage =
    location.pathname?.includes('my-profile') || location.pathname.includes('favorites') || location.pathname.includes('library-update');

  const mutation = useMutation(signInUser, {
    onError: (error) => {
      console.error('Mutation error:', error);
    },
    onSuccess: (data) => {
      if (data) {
        localStorage.setItem('SelectedLibraryData', JSON.stringify({ SelectedLibraryData: data }));
      }
    },
  });

  useEffect(() => {
    let token = '';
    try {
      const storedToken = localStorage.getItem('inplayer_token');
      if (storedToken) {
        token = JSON.parse(storedToken).token;
        mutation.mutate(token);
      }
    } catch (error) {
      console.error('Error parsing token from localStorage:', error);
    }
  }, []);

  const onLogout = useCallback(async () => {
    // Empty customer on a user page leads to navigate (code bellow), so we don't repeat it here
    await accountController.logout();
    localStorage.removeItem('SelectedLibraryData');
    // navigate('/', { replace: true });
    Cookies.remove('authData', { domain: COOKIE_DOMAIN, path: '/' });
    Cookies.remove('SelectedLibraryData', { domain: COOKIE_DOMAIN, path: '/' });
    window.location.href = isProduction ? 'https://auth.bibliotele.com/' : 'https://auth1.bibliotele.com/';
  }, [accountController]);

  const redirect = (val: boolean) => {
    if (val) {
      return navigate(modalURLFromLocation(location, 'Library-auto-complete'));
    }
    let token = '';
    try {
      const storedToken = localStorage.getItem('inplayer_token');
      if (storedToken) {
        token = JSON.parse(storedToken).token;
        mutation.mutate(token);
      }
    } catch (error) {
      console.error('Error parsing token from localStorage:', error);
    }
    // withInOrganization is false user should not able to update a library
    if (withInOrganization[0]) {
      navigate(modalURLFromLocation(location, 'library-access'), {
        state: {
          isComeFrom: 'login',
          isComeFromEditPage: true,
          libraryId: libraryBranchId[0],
          libraryParent: libraryParents[0],
          withInOrganization: withInOrganization[0],
          libraryName: libraryBranchName,
          libraryType: libraryType[0],
        },
      });
    } else navigate(modalURLFromLocation(location, 'Library-Purchase-Prompt'));
  };

  useEffect(() => {
    if (!loading && !customer) {
      navigate('/', { replace: true });
    }
  }, [navigate, customer, loading]);

  if (!customer) {
    return (
      <div className={styles.user}>
        <LoadingOverlay inline />
      </div>
    );
  }
  return (
    <div className={styles.user}>
      {isLargeScreen && (
        <nav className={styles.leftColumn}>
          <div className={styles.panel}>
            <ul>
              {accessModel === 'SVOD' && profilesEnabled && profileAndFavoritesPage && (
                <li>
                  <Button
                    to={profile ? userProfileURL(profile.id, true) : undefined}
                    label={profile?.name ?? t('user:nav.profile')}
                    variant="text"
                    startIcon={<img className={styles.profileIcon} src={profile?.avatar_url} alt={profile?.name} />}
                    className={styles.button}
                  />
                </li>
              )}
              {(!profilesEnabled || !profileAndFavoritesPage) && (
                <li>
                  <Button
                    to={RELATIVE_PATH_USER_ACCOUNT}
                    label={t('user:nav.account')}
                    variant="text"
                    startIcon={<Icon icon={AccountCircle} />}
                    className={styles.button}
                  />
                </li>
              )}
              {favoritesList && (!profilesEnabled || profileAndFavoritesPage) && (
                <li>
                  <Button
                    to={RELATIVE_PATH_USER_FAVORITES}
                    label={t('user:nav.favorites')}
                    variant="text"
                    startIcon={<Icon icon={Favorite} />}
                    className={styles.button}
                  />
                </li>
              )}

              {/* {accessModel !== ACCESS_MODEL.AVOD &&(!profilesEnabled || !profileAndFavoritesPage) && (
                <li>
                  <Button
                    to={RELATIVE_PATH_USER_PAYMENTS}
                    label={t('nav.payments')}
                    variant="text"
                    startIcon={<Icon icon={BalanceWallet} />}
                    className={styles.button}
                  />
                </li>
              )} */}
              {(!profilesEnabled || profileAndFavoritesPage) && (
                <li>
                  <Button
                    to={RELATIVE_PATH_USER_LIBRARY_ACCESS}
                    label={t('account:Library_Access.Library_valid_screen.Manage_Library')}
                    variant="text"
                    startIcon={<Icon icon={LibraryBooksIcon} />}
                    className={styles.button}
                  />
                </li>
              )}

              {(!profilesEnabled || !profileAndFavoritesPage) && (
                <li className={styles.logoutLi}>
                  <Button onClick={onLogout} label={t('user:nav.logout')} variant="text" startIcon={<Icon icon={Exit} />} className={styles.button} />
                </li>
              )}
            </ul>
          </div>
        </nav>
      )}
      <div
        className={
          location.pathname.includes('library-update') && !libraryParent.length && !libraryBranchName.length ? styles.mainColumn_center : styles.mainColumn
        }
      >
        <Routes>
          <Route
            path={RELATIVE_PATH_USER_ACCOUNT}
            element={<AccountComponent panelClassName={styles.panel} panelHeaderClassName={styles.panelHeader} canUpdateEmail={canUpdateEmail} />}
          />
          {profilesEnabled && <Route path={RELATIVE_PATH_USER_MY_PROFILE} element={<EditProfile contained />} />}
          {favoritesList && (
            <Route
              path={RELATIVE_PATH_USER_FAVORITES}
              element={
                <>
                  <Favorites
                    playlist={favorites}
                    onClearFavoritesClick={() => setClearFavoritesOpen(true)}
                    accessModel={accessModel}
                    hasSubscription={!!subscription}
                  />
                  <ConfirmationDialog
                    open={clearFavoritesOpen}
                    title={t('user:favorites.clear_favorites_title')}
                    body={t('user:favorites.clear_favorites_body')}
                    onConfirm={async () => {
                      await favoritesController.clear();
                      setClearFavoritesOpen(false);
                    }}
                    onClose={() => setClearFavoritesOpen(false)}
                  />
                </>
              }
            />
          )}
          <Route
            path={RELATIVE_PATH_USER_PAYMENTS}
            element={accessModel !== ACCESS_MODEL.AVOD ? <PaymentContainer /> : <Navigate to={RELATIVE_PATH_USER_ACCOUNT} />}
          />
          <Route path="*" element={<Navigate to={RELATIVE_PATH_USER_ACCOUNT} />} />
          <Route
            path={RELATIVE_PATH_USER_LIBRARY_ACCESS}
            element={
              libraryParent?.length && libraryBranchName?.length ? (
                <section className={styles.panel}>
                  <h2 className={styles.library_heading}>{t('account:Library_Access.Library_valid_screen.Library_Details')}</h2>
                  <section className={styles.edit_section}>
                    <div className={styles.library_name}>
                      <h4 className={styles.libararyparentName}>{libraryParent[0].toLowerCase()}</h4>
                      <p className={styles.branch_name}> {libraryBranchName[0]?.toLowerCase()}</p>
                      {!!formattedDate && (
                        <p className={styles.branch_name}>
                          {t('account:Library_Access.Library_valid_screen.Valid_upto')} : {formattedDate}
                        </p>
                      )}
                    </div>
                    {libraryType[0] === 'academic'
                      ? null
                      : subscriptionDetail.SelectedLibraryData.subscriptionDetail.subType !== 'premium' && (
                          <Button
                            onClick={redirect.bind(null, false)}
                            label={t('account:Library_Access.Library_valid_screen.Edit_library')}
                            variant="outlined"
                            className={styles.edit_library_btn}
                          />
                        )}
                  </section>
                </section>
              ) : (
                <section className={styles.empty_message}>
                  <h2>{t('account:Library_Access.Library_valid_screen.No_Library_Details')}</h2>
                  <Button
                    onClick={redirect.bind(null, true)}
                    label={t('account:Library_Access.Library_valid_screen.Add_Library')}
                    variant="contained"
                    className={styles.add_library_btn}
                    startIcon={<Icon icon={Close} />}
                  />
                </section>
              )
            }
          />
        </Routes>
      </div>
    </div>
  );
};

export default User;
